import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { createDeepCopy, loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";
import { IndependentDynamicForm } from "@companion-core/shared/app/Components/DynamicForm/IndependentDynamicForm";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { WizardStoreProvider } from "@companion-core/shared/app/Store/wizardStore";
import { DynamicFormJson, Field } from "@companion-core/shared/app/Interfaces/wizard";
import { useAssistance } from "@companion-core/shared/app/Queries/assistance";
import {
  trackClickNeedHelpSubmit,
  trackPageNeedHelp,
  trackPageNeedHelpConfirmation,
} from "@companion-core/shared/app/tracking/needHelp";
import { formatPayload } from "@companion-core/shared/app/Utils/assistance";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { PopIn } from "@companion-core/shared/app/Components/PopIn";
import { useMemberData } from "@companion-core/shared/app/Hooks/member";

import "@companion-core/web/src/Assets/Styles/scss/needHelp.scss";
import { APIMemberData } from "@companion-core/shared/app/Interfaces/API";

export const NeedHelp = () => {
  const queryClient = useQueryClient();
  const { data: memberData, isPending: isMemberPending } = useMemberData();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { config } = useConfigStore((state) => ({
    config: state.config,
  }));
  const { authenticated } = useAuthStore((state) => ({ authenticated: state.authenticated }));
  const prefillForm = useCallback(
    (form: DynamicFormJson) => {
      // Form fields are named after the member attributes they prefill.
      const memberFields = memberData as Record<string, Field["value"]> | undefined;
      form?.steps[0]?.fields.forEach((field: Field) => {
        if (memberFields?.[field.name]) field.value = memberFields[field.name];
      });
      return form;
    },
    [memberData]
  );
  const needHelpConfigModule = useMemo(() => config?.modules?.["need-help"], [config]);
  const urlBanner = useMemo(() => needHelpConfigModule?.options?.img, [needHelpConfigModule]);
  const { specificFormOn, specificFormOff } = useMemo(
    () => needHelpConfigModule?.options?.forms ?? {},
    [needHelpConfigModule]
  );
  const formName = useMemo(
    () => (authenticated ? (specificFormOn ?? "needHelpOn") : (specificFormOff ?? "needHelpOff")),
    [authenticated, specificFormOn, specificFormOff]
  );
  const baseJson = useMemo(() => loadFormFromPath(`common-forms/needHelp/${formName}`), [formName]);
  const [formInstance, setFormInstance] = useState<number>(0);
  // Prefilling writes into the field objects, and the wizard store keeps references to them once
  // the form is initialised: it must always run on a copy the mounted form does not own yet.
  const jsonForm = useMemo(
    () => prefillForm(createDeepCopy(baseJson)),
    [prefillForm, baseJson, formInstance]
  );
  // The member query is disabled while unauthenticated, so `isPending` never settles in that case.
  const isMemberDataReady = !authenticated || !isMemberPending;
  const [requestError, setRequestError] = useState<boolean>(false);
  const [isValidationPopinOpen, setIsValidationPopinOpen] = useState<boolean>(false);
  const { mutateAsync: sendAssistanceRequest } = useAssistance();

  useEffect(() => {
    trackPageNeedHelp();
    if (!queryClient.isFetching({ queryKey: ["member"] })) {
      queryClient.invalidateQueries({ queryKey: ["member"] });
    }
  }, []);

  const onSubmitForm = async (payload: Record<string, unknown>) => {
    trackClickNeedHelpSubmit();
    const formattedPayload = formatPayload(payload, memberData as APIMemberData);
    try {
      await sendAssistanceRequest({ payload: { ...formattedPayload } });
      setRequestError(false);
      trackPageNeedHelpConfirmation({ error: false });
    } catch (error) {
      setRequestError(true);
      trackPageNeedHelpConfirmation({ error: true });
      throw error instanceof Error ? error : new Error(String(error));
    } finally {
      setIsValidationPopinOpen(true);
    }
  };

  return (
    <WizardStoreProvider>
      <div
        ref={scrollRef}
        data-testid="need-help"
        className="need-help d-flex flex-direction-column align-items-center">
        {!isMobileSize && (
          <div data-testid="need-help__banner" className="need-help__banner">
            <Banner
              title={t("need-help.description.title")}
              imageUrl={urlBanner}
              boxShadow={true}
              disappearOnMobileSize
            />
          </div>
        )}
        <div data-testid="need-help__title" className="need-help__title">
          <h2 className="super-title-3">{t("need-help.description.subtitle")}</h2>
        </div>
        <div data-testid="need-help__form" className="need-help__form">
          <PopIn
            testID="need-help__popin"
            icon={requestError ? "JP-Circle-close" : "JP-Circle-check"}
            isOpen={isValidationPopinOpen}
            title={requestError ? t("need-help.error.title") : t("need-help.confirmation.title")}
            onClose={() => {
              setRequestError(false);
              setIsValidationPopinOpen(false);
            }}
            primaryButton={{
              text: t("need-help.confirmation.cta"),
              onClick: () => {
                setRequestError(false);
                setIsValidationPopinOpen(false);
              },
            }}
          />
          <div data-testid="need-help__text" className="body-text-4">
            <div className="need-help__text">{t("need-help.description.text")}</div>
            <div className="need-help__tips">{t("need-help.description.tips")}</div>
          </div>
          <div className="margin-top-16">
            {isMemberDataReady && (
              <IndependentDynamicForm
                key={formInstance}
                name={formName}
                initialFormJSON={jsonForm}
                onSubmitForm={onSubmitForm}
                onSubmitConclude={() => setFormInstance((instance) => instance + 1)}
              />
            )}
          </div>
        </div>
      </div>
    </WizardStoreProvider>
  );
};
